// ─────────────────────────────────────────────────────────────────────────
//  ClickViewer · web · 밝은 화면 / 어두운 화면
//
//  ★★★ 기본은 **light 고정**이다. `auto` 가 아니다. ★★★
//
//  왜: 폰의 자동 다크 전환은 **조도가 아니라 일몰 시각**으로 켜진다. 여름 19:30 일몰이면
//  그 시각의 현장은 아직 환한데 화면만 어두워진다 — 「밖은 밝은데 화면은 어두운」
//  최악의 조합이다. 그리고 그때 어두운 화면의 muted 는 야외에서 **1.64:1** 이다.
//
//  실측으로도 어두운 화면은 **야외에서 모든 쌍이 밝은 화면보다 나쁘다**
//  (반사광 모델: 화면 800nit · 반사율 4.5%, 20,000lux):
//
//      어두운 ink   3.14  <  밝은 ink   3.66
//      어두운 accent 버튼 2.20  <  밝은 accent 버튼 2.87
//      어두운 lineStrong 1.40  <  밝은 lineStrong 2.20   ← 야외에서 **입력칸 테두리가 사라진다**
//
//  ★ 그래도 어두운 화면을 **만들기는 한다.** 터널·야간 점검·차 안에서는 필요하다.
//    단 **수동 토글**로만 켠다. 그리고 저장은 계정이 아니라 **기기별**(localStorage)이다 —
//    같은 사람이 사무실 PC 와 현장 폰에서 서로 다른 것을 원한다.
//
//  ⚠️ 진짜 한 단어짜리 고침은 main.tsx 의 `defaultColorScheme="auto"` → `"light"` 다.
//     그 파일은 지금 다른 사람이 배선 중이라 건드리지 않았다. 대신 아래 seedLightDefault()
//     가 **MantineProvider 가 뜨기 전에** localStorage 를 채워서 같은 결과를 만든다.
//     main.tsx 가 light 로 바뀌면 이 함수는 그냥 아무 일도 안 하게 된다(무해).
// ─────────────────────────────────────────────────────────────────────────

import { useEffect } from 'react';
import { Group, SegmentedControl, Text, useMantineColorScheme } from '@mantine/core';

/**
 * Mantine 의 색 모드 저장 키.
 * 근거: `@mantine/core/esm/core/MantineProvider/color-scheme-managers/local-storage-manager.mjs:4`
 * (`key = 'mantine-color-scheme-value'`, 값은 `'light' | 'dark' | 'auto'` 평문)
 *
 * ⚠️ Mantine 을 크게 올리는 날 이 키가 바뀌면 조용히 auto 로 돌아간다. 그때 증상은
 *   "여름 저녁에 화면이 갑자기 어두워짐" 이다. 위 파일을 다시 확인해라.
 */
const STORAGE_KEY = 'mantine-color-scheme-value';

/**
 * 저장된 선택이 **없을 때만** light 를 심는다.
 *
 * ★ 모듈 본문에서 부른다(아래 마지막 줄). ES 모듈은 import 가 먼저 평가되므로
 *   이 파일을 import 하는 Shell.tsx → App.tsx → main.tsx 순서상 **createRoot().render()
 *   보다 먼저** 실행된다. 즉 MantineProvider 가 저장소를 읽는 시점에 이미 값이 있다.
 *   → 어두운 OS 를 쓰는 폰에서도 **어두운 화면이 한 프레임도 안 보인다.**
 *
 * ⚠️ 사용자가 직접 dark 를 골랐으면 그대로 둔다. 여기서 매번 light 로 되돌리면
 *   토글이 "눌리는데 안 바뀌는 버튼" 이 된다.
 */
export function seedLightDefault(): void {
  try {
    const saved = window.localStorage.getItem(STORAGE_KEY);
    if (saved === 'light' || saved === 'dark') return;
    // 저장값이 없거나 'auto' 면 light 로 못박는다. auto 를 남겨 두면 일몰에 뒤집힌다.
    window.localStorage.setItem(STORAGE_KEY, 'light');
    document.documentElement.setAttribute('data-mantine-color-scheme', 'light');
  } catch {
    // 사생활 보호 모드 등에서 localStorage 가 막힐 수 있다. 그때는 그냥 넘어간다 —
    // 색 모드 하나 때문에 앱이 안 뜨는 것이 훨씬 나쁘다.
  }
}

/**
 * 폰 브라우저 주소창 색을 지금 화면에 맞춘다.
 *
 * ★ index.html 에 `media="(prefers-color-scheme: …)"` 두 줄로 두면 **틀린다.**
 *   우리는 OS 설정이 아니라 사용자의 수동 선택을 따르므로, 어두운 OS + 우리 light 조합에서
 *   주소창만 까맣게 남는다. 화면 위쪽 20px 이 본문과 다른 색이면 사람은 그걸
 *   "덜 만든 화면" 으로 읽는다.
 */
export function useThemeColorMeta(): void {
  const { colorScheme } = useMantineColorScheme();
  useEffect(() => {
    const dark = document.documentElement.getAttribute('data-mantine-color-scheme') === 'dark';
    const meta = document.querySelector('meta[name="theme-color"]');
    if (meta) meta.setAttribute('content', dark ? '#101613' : '#F6F8F6');
    // color-scheme 은 브라우저 기본 위젯(스크롤바·폼)의 색을 맞춘다.
    document.documentElement.style.colorScheme = dark ? 'dark' : 'light';
  }, [colorScheme]);
}

/**
 * 화면 밝기 선택. **설정 화면에 둔다. 머리글에 두지 마라** —
 * 머리글의 자리는 좁고, 거기 있는 버튼은 급할 때 잘못 눌린다.
 *
 * ⚠️ 「자동」을 **일부러 뺐다.** 항목이 셋이면 사람은 자동을 고르고, 그러면 여름 저녁마다
 *    화면이 어두워진다(위 머리말). 필요한 것은 "지금 여기서 어느 쪽이 잘 보이나" 뿐이다.
 */
export function ColorSchemeControl() {
  const { colorScheme, setColorScheme } = useMantineColorScheme();
  const value = colorScheme === 'dark' ? 'dark' : 'light';

  return (
    <Group gap="sm" wrap="wrap">
      <Text size="md" fw={600} id="cv-scheme-label">
        화면 밝기
      </Text>
      <SegmentedControl
        aria-labelledby="cv-scheme-label"
        value={value}
        onChange={(v) => setColorScheme(v === 'dark' ? 'dark' : 'light')}
        data={[
          { value: 'light', label: '밝게 (야외)' },
          { value: 'dark', label: '어둡게 (터널·야간)' },
        ]}
        // 터치 44px. SegmentedControl 은 기본이 그보다 낮다.
        styles={{ label: { minHeight: 40, display: 'flex', alignItems: 'center' } }}
      />
    </Group>
  );
}

// ★ 모듈 본문에서 한 번. 이유는 seedLightDefault() 머리말에 있다.
seedLightDefault();
